import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function NewsletterSection() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      setEmail('');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qa5due3oq4jqumtj50', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      if (response.ok) {
        setStatus('success');
        setEmail('');
        setTimeout(() => setStatus('idle'), 3000);
      } else {
        setStatus('error');
        setErrorMsg(responseText || t('subscribeError'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('subscribeError'));
    }
  };

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50">
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
          {t('newsletterTitle')}
        </h2>
        <p className="text-foreground-500 text-sm md:text-base mb-8">
          {t('newsletterDesc')}
        </p>

        <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="honeypot-field"
          />
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('subscribePlaceholder')}
            required
            className="flex-1 px-5 py-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-300"
          />
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 whitespace-nowrap"
          >
            {status === 'submitting' ? t('loading') : t('subscribeButton')}
          </button>
        </form>

        {status === 'success' && (
          <p className="mt-4 text-sm text-accent-700 bg-accent-100/50 inline-block px-4 py-2 rounded-full">
            {t('subscribeSuccess')}
          </p>
        )}
        {status === 'error' && (
          <p className="mt-4 text-sm text-primary-700 bg-primary-100/50 inline-block px-4 py-2 rounded-full">
            {errorMsg}
          </p>
        )}
      </div>
    </section>
  );
}